import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { OrderByItem } from "../../../api/bigQuery/pharmaStpFileOrders";
import { FilterValues } from "../../../data-model/filterValues";
import { processFilterData } from "../utils/processFilterData";

export interface PharmaStpFileFilterPayload {
  date_from_filename_start?: string;
  date_from_filename_end?: string;
  filters?: {
    patient_mrn?: string[];
    [key: string]: any;
  };
  page: number;
  page_size: number;
  request_id?: string;
  order_by?: OrderByItem[];
}

export interface UseFilterPayloadReturn {
  filterPayload: PharmaStpFileFilterPayload | null;
  setFilterPayload: React.Dispatch<
    React.SetStateAction<PharmaStpFileFilterPayload | null>
  >;
}

export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
): UseFilterPayloadReturn => {
  const [filterPayload, setFilterPayload] =
    useState<PharmaStpFileFilterPayload | null>(null);

  useEffect(() => {
    if (!initialDataFetchingExecuted) {
      setFilterPayload(null);
    } else {
      const { filters: apiFilters, dateRange } =
        processFilterData(validFilters);

      const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
      const hasFilters = validFilters && validFilters.length > 0;
      if (hasSearchTerm) {
        const mrnArray = searchTerm
          .split(",")
          .map((mrn) => mrn.trim())
          .filter((mrn) => mrn.length > 0);

        if (mrnArray.length > 0) {
          apiFilters.patient_mrn = mrnArray;
        }
      }

      // Only omit dates when searching and no filter is applied (backend searches all dates).
      // When any filter is applied, keep dates and still send search term if present.
      const omitDates = hasSearchTerm && !hasFilters;
      const payload: PharmaStpFileFilterPayload = {
        ...(omitDates
          ? {}
          : {
              date_from_filename_start: dateRange?.startDate ?? dateFromStart,
              date_from_filename_end: dateRange?.endDate ?? dateFromEnd,
            }),
        filters: apiFilters,
        page: 1,
        page_size: pageSize,
        request_id: uuidv4(),
        order_by: [
          { column: "filename", direction: "DESC" },
          { column: "patient_mrn", direction: "ASC" },
          { column: "drug", direction: "ASC" },
        ],
      };
      setFilterPayload(payload);
    }
  }, [
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
  ]);

  return {
    filterPayload,
    setFilterPayload,
  };
};
